import React from "react";
import { useNavigate } from "react-router-dom";
import {
  CasinoTagsEnum,
  getSectionGameImage,
  getSectionGameLabel,
  getSectionGameNavigationPath,
  isProviderCategorySectionEntry,
  HOME_ROUTE,
  INT_CASINO_ROUTE,
  SectionGame,
  useHomeSectionGames,
} from "@hypexone/uicore";
import { SVLS_API_CLIENT } from "../../../App.utils";

interface HomeGamesProps {
  isLoggedIn: boolean;
  showPopUp: () => void;
}

const HomeGames: React.FC<HomeGamesProps> = ({ isLoggedIn, showPopUp }) => {
  const { games: trendingGames } = useHomeSectionGames(
    SVLS_API_CLIENT,
    CasinoTagsEnum.TRENDING
  );
  const { games: newLaunchGames } = useHomeSectionGames(
    SVLS_API_CLIENT,
    CasinoTagsEnum.NEW_LAUNCH + "_laser"
  );
  const { games: recommendedGames } = useHomeSectionGames(
    SVLS_API_CLIENT,
    CasinoTagsEnum.RECOMMENDED_GAMES
  );
  const { games: liveCasinoGames } = useHomeSectionGames(
    SVLS_API_CLIENT,
    CasinoTagsEnum.LIVE_CASINO_GAMES
  );
  const { games: slotsGames } = useHomeSectionGames(
    SVLS_API_CLIENT,
    CasinoTagsEnum.SLOTS
  );
  const navigate = useNavigate();

  let loggedInUserStatus;
  if (isLoggedIn) {
    loggedInUserStatus = JSON.parse(
      window.atob(sessionStorage.getItem("jwt_token").split(".")[1])
    ).status;
  }

  const handleGameClick = (game: SectionGame) => {
    const path = getSectionGameNavigationPath(
      game,
      INT_CASINO_ROUTE,
      "subProvider"
    );
    if (!path) return;

    if (isProviderCategorySectionEntry(game)) {
      navigate(path);
      return;
    }

    if (!isLoggedIn) {
      showPopUp();
      return;
    }

    if (loggedInUserStatus === 0 || loggedInUserStatus === 3) {
      navigate(HOME_ROUTE);
      return;
    }

    navigate(path, { state: { gameName: game.gameName } });
  };

  const GameCard = ({ game }: { game: SectionGame }) => {
    const launchGame = () => handleGameClick(game);
    return (
    <div className="flex flex-col flex-shrink-0 w-[33%] md:w-[20%] lg:w-[14.28%] xl:w-[10%] h-[160px] md:h-[180px] max-h-[160px] md:max-h-[180px]">
      <div className="flex-1 overflow-hidden">
        <img
          loading="lazy"
          src={getSectionGameImage(game)}
          alt={getSectionGameLabel(game)}
          className="w-full h-full cursor-pointer"
          onClick={launchGame}
        />
      </div>
      <div className="flex items-center justify-center text-center text-[#fff] bg-black w-full h-[32px] sm:h-[24px] cursor-pointer p-[5px] text-[11px] font-bold flex-shrink-0">
        <span className="truncate w-full">{getSectionGameLabel(game)}</span>
      </div>
    </div>
    );
  };

  const CategorySection = ({
    title,
    games,
  }: {
    title: string;
    games: SectionGame[];
  }) => (
    <div className="flex flex-col gap-0.5">
      <h2
        style={{
          background: "linear-gradient(-180deg, #F72424 0%, #BB1C00 100%)",
        }}
        className="text-[13px] md:text-[16px] px-1.5 py-1 text-white"
      >
        {title}
      </h2>
      <div className="flex items-center gap-1 overflow-x-auto hide-scrollbar">
        {games?.map((game, gameIndex) => (
          <GameCard
            key={(game.gameId || game.id?.toString() || "") + gameIndex}
            game={game}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-0.5">
      {newLaunchGames.length > 0 && (
        <CategorySection title="NEW LAUNCH" games={newLaunchGames} />
      )}
      {trendingGames.length > 0 && (
        <CategorySection title="TRENDING GAMES" games={trendingGames} />
      )}
      {recommendedGames.length > 0 && (
        <CategorySection title="RECOMMENDED GAMES" games={recommendedGames} />
      )}
      {liveCasinoGames.length > 0 && (
        <CategorySection title="LIVE CASINO GAMES" games={liveCasinoGames} />
      )}
      {slotsGames.length > 0 && (
        <CategorySection title="SLOTS" games={slotsGames} />
      )}
    </div>
  );
};

export default HomeGames;
